import { Blueprint, BuildCmd, ConfigCmd, FixedAngle, Item, Shape } from "dsabp-js"
import { Canvas, CanvasRenderingContext2D, createCanvas, Image } from "./canvas.js"
import { connectedTileTypes } from "./renderers/Tile.js"
import { RenderResult } from "./types.js"
import { getDrawItemData, getShipBackgroundData } from "./util.js"

export const MAX_SIZE = 80

export class BpRenderer {
	declare canvasOut: Canvas
	declare canvasPaint: Canvas
	declare canvasMain: Canvas
	declare canvasTop: Canvas

	declare ctxOut: CanvasRenderingContext2D
	declare ctxPaint: CanvasRenderingContext2D
	declare ctxMain: CanvasRenderingContext2D
	declare ctxTop: CanvasRenderingContext2D


	width: number

	height: number

	renderCache: Record<string, RenderResult[]> = {}
	imageCache: Record<string, Image> = {}
	knownObjectTypes = new Set([...connectedTileTypes])
	knownObjects: Item[][] = []
	squareSize = 40

	zIndexMap = new Map<Item, number>([
		[Item.LOADER_NEW, 2],
		[Item.EXPANDO_BOX, 1],
		[Item.RCD_FLUX, 1],
		[Item.RCD_SANDBOX, 1]
	])


	customObjectByCoordsFn: (x: number, y: number) => Item


	constructor(outCanvas?: Canvas) {
		if (outCanvas === null)
			return

		this.canvasPaint = createCanvas(0, 0)
		this.ctxPaint = this.canvasPaint.getContext("2d")

		this.canvasMain = createCanvas(0, 0)
		this.ctxMain = this.canvasMain.getContext("2d")

		this.canvasTop = createCanvas(0, 0)
		this.ctxTop = this.canvasTop.getContext("2d")

		this.canvasOut = outCanvas ?? createCanvas(0, 0)
		this.ctxOut = this.canvasOut.getContext("2d")
	}


	setSize(width: number, height: number, exteriorMargin = 3.75) {
		if (width < 1) width = 1
		if (height < 1) height = 1

		this.width = width
		this.height = height

		if (this.canvasMain) {
			const marginPx = Math.ceil(exteriorMargin * this.squareSize) // translate hates decimals
			width = width * this.squareSize + (marginPx * 2)
			height = height * this.squareSize + (marginPx * 2)

			this.canvasPaint.width = this.canvasMain.width = this.canvasTop.width = width
			this.canvasPaint.height = this.canvasMain.height = this.canvasTop.height = height
			this.ctxTop.translate(marginPx, marginPx)
			this.ctxMain.translate(marginPx, marginPx)
			this.ctxPaint.translate(marginPx, marginPx)

			if (this.canvasOut) {
				this.canvasOut.width = width
				this.canvasOut.height = height
			}
		}
	}


	toRel(x: number, y: number) {
		return { x: x / 40, y: y / 40 }
	}


	async render(bp: Blueprint, exteriorMargin = 3.75) {
		const startTime = performance.now()

		this.setSize(bp.width, bp.height, exteriorMargin)
		const bgData = await getShipBackgroundData(this, bp)
		for (let x = 0; x < bgData.tiles.length; x++) {
			for (let y = 0; y < bgData.tiles[x].length; y++) {
				const tileData = bgData.tiles[x][y];
				(tileData.type == "paint" ? this.ctxPaint : this.ctxMain).drawImage(tileData.image, x * this.squareSize, y * this.squareSize, this.squareSize, this.squareSize)
			}
		}

		const drawOperations: { item: Item, x: number, y: number, shape: Shape, config: ConfigCmd, hullDirection: FixedAngle, zIndex?: number }[] = []

		const addDrawOperation = (item: Item, x: number, y: number, shape: Shape, config: ConfigCmd) => {
			const bgTileType = bgData.tiles[x]?.[y]?.type
			drawOperations.push({
				item, x, y, shape, config,
				hullDirection: (
					bgTileType && bgTileType != "paint" && item.buildInfo?.[0].allow_world
						? bgTileType == "wallH"
							? (y == 0 ? FixedAngle.DOWN : FixedAngle.UP)
							: bgTileType == "wallV"
								? (x == 0 ? FixedAngle.LEFT : FixedAngle.RIGHT)
								: y == 0 ? FixedAngle.DOWN : FixedAngle.UP // corners
						: null
				),
				zIndex: (this.zIndexMap.get(item) ?? 0) + (item.buildInfo?.[0].allow_world ? 3 : 0)
			})
		}

		let config: ConfigCmd
		for (const cmd of bp.commands) {
			if (cmd instanceof ConfigCmd) {
				config = cmd
				continue
			} else if (cmd instanceof BuildCmd) {
				if (cmd.bits) {
					let bitI = 0
					for (const bit of cmd.bits) {
						if (bit)
							addDrawOperation(cmd.item, cmd.x + bitI, cmd.y, cmd.shape, config)
						bitI++
					}
				} else {
					addDrawOperation(cmd.item, cmd.x, cmd.y, cmd.shape, config)
				}
			}
		}

		for (const op of drawOperations.sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0)))
			await drawItem(this, op.item, op.x, op.y, op.shape, op.config, op.hullDirection)

		if (this.ctxOut) this.draw()
		return performance.now() - startTime
	}

	draw() {
		this.ctxOut.drawImage(this.canvasPaint, 0, 0)
		this.ctxOut.drawImage(this.canvasMain, 0, 0)
		this.ctxOut.drawImage(this.canvasTop, 0, 0)
	}

	getKnownObject(x: number, y: number) {
		if (this.customObjectByCoordsFn != null)
			return this.customObjectByCoordsFn(x, y)
		return this.knownObjects[x]?.[y]
	}

	clearCache() {
		for (const key in this.imageCache)
			delete this.imageCache[key]
		for (const key in this.renderCache)
			delete this.renderCache[key]
	}

	useCacheOf(bpRenderer: BpRenderer) {
		this.imageCache = bpRenderer.imageCache
		this.renderCache = bpRenderer.renderCache
	}
}

async function drawItem(main: BpRenderer, item: Item, x: number, y: number, shape: Shape, config: ConfigCmd, hullDirection?: FixedAngle) {
	for (const data of await getDrawItemData(main, item, x, y, shape, config, hullDirection)) {
		const { layer, image, angle, sx, sy, sw, sh, pos, drawPosOff, off, size } = data
		const ctx = (layer ?? "main") == "main" ? main.ctxMain : main.ctxTop

		const drawPos = { x: pos.x + drawPosOff.x, y: pos.y + drawPosOff.y }

		if (hullDirection || angle != null) {
			const ang = hullDirection ? (-hullDirection.enumValue * 90) : angle
			ctx.save()
			ctx.translate(drawPos.x + off.x, drawPos.y + off.y)
			ctx.rotate(ang * (Math.PI / 180))

			const [x, y, w, h] =
				hullDirection
					? Math.abs(ang) % 180 != 0
						? [-off.y, -off.x, size.h, size.w]
						: [-off.x, -off.y, size.w, size.h]
					: [-off.x, -off.y, size.w, size.h]

			ctx.drawImage(image, sx ?? 0, sy ?? 0, sw ?? image.width, sh ?? image.height, x, y, w, h)
			ctx.restore()
		} else {
			ctx.drawImage(image, sx ?? 0, sy ?? 0, sw ?? image.width, sh ?? image.height, drawPos.x, drawPos.y, size.w, size.h)
		}
	}
}
